import React, { useEffect } from 'react';
import { pascalCase } from 'change-case';
import { defineMessages, FormattedMessage, useIntl } from 'react-intl-next';

import { useAnalyticsEvents } from '@atlaskit/analytics-next';
import Banner from '@atlaskit/banner';
import { IconButton } from '@atlaskit/button/new';
import Link from '@atlaskit/link';
import { Inline, Text } from '@atlaskit/primitives';
import CrossIcon from '@atlaskit/icon/core/migration/close--cross';
import WarningIcon from '@atlaskit/icon/core/migration/warning';

import type { ConfluenceEdition } from '@confluence/session-data';

const messages = defineMessages({
	trialEndsWarning: {
		id: 'experiment-eot-banner.trial-ends-warning',
		defaultMessage:
			'Your {edition} trial ends in {daysLeftInTrial, plural, one {# day} other {# days}}.',
		description: 'Inform the user that their trial is ending soon',
	},
	linkToPaymentDetails: {
		id: 'experiment-eot-banner.link-to-payment-details',
		defaultMessage:
			'<link>Add payment</link> to keep advanced features like unlimited users and page permissions.',
		description: 'CTA link to add payment details, with some benefits described',
	},
	dismissIcon: {
		id: 'experiment-eot-banner.icons.dismiss',
		defaultMessage: 'Dismiss button with cross icon',
		description: 'Accessibility label for an dismiss icon',
	},
});

interface EotBannerProps {
	edition: ConfluenceEdition;
	paymentFlowUrl: string;
	daysLeftInTrial: number;
	/** The parent needs to do some work when the user dismisses the banner */
	onDismiss: () => void;
}

/**
 * The UI for the end-of-trial banner.
 */
export const EotBanner = ({
	edition,
	paymentFlowUrl,
	daysLeftInTrial,
	onDismiss,
}: EotBannerProps) => {
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const { formatMessage } = useIntl();

	useEffect(() => {
		createAnalyticsEvent({
			type: 'sendScreenEvent',
			data: {
				name: 'EotBanner',
				attributes: {
					daysLeftInTrial,
					edition,
				},
			},
		}).fire();
	}, [createAnalyticsEvent, daysLeftInTrial, paymentFlowUrl, edition]);

	const onClickPay = () => {
		createAnalyticsEvent({
			type: 'sendUIEvent',
			data: {
				source: 'EotBanner',
				action: 'clicked',
				actionSubject: 'button',
				actionSubjectId: 'addPayment',
				attributes: {
					daysLeftInTrial,
					edition,
				},
			},
		}).fire();
	};

	/*
	 * Handles analytics in addition to the parent's `onDismiss()`.
	 */
	const handleDismissal = () => {
		onDismiss();
		createAnalyticsEvent({
			type: 'sendUIEvent',
			data: {
				action: 'dismissed',
				actionSubject: 'button',
				actionSubjectId: 'dismissIcon',
				source: 'EotBanner',
				attributes: {
					daysLeftInTrial,
					edition,
				},
			},
		}).fire();
	};

	return (
		<Banner
			appearance="warning"
			icon={<WarningIcon label="Warning icon" spacing="spacious" color="currentColor" />}
		>
			<Inline space="space.150" alignBlock="center">
				<Text>
					<FormattedMessage
						{...messages.trialEndsWarning}
						values={{
							edition: pascalCase(edition),
							daysLeftInTrial,
						}}
					/>
					&nbsp;
					<FormattedMessage
						{...messages.linkToPaymentDetails}
						values={{
							link: (chunks) => (
								<Link onClick={onClickPay} target="_blank" href={paymentFlowUrl}>
									{chunks}
								</Link>
							),
						}}
					/>
				</Text>
				<IconButton
					icon={CrossIcon}
					appearance="subtle"
					onClick={handleDismissal}
					label={formatMessage(messages.dismissIcon)}
				/>
			</Inline>
		</Banner>
	);
};
